/**
 * @fileoverview Spanish translations.
 * @package PlayItNow
 * @module apps/front/lib/i18n/locales/es
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

export const es = {
  login: {
    title: 'PlayItNow',
    subtitle: 'Inicia sesión en tu espacio de administración',
    emailLabel: 'Email o nombre de usuario',
    emailPlaceholder: 'tu@email.com o nombre de usuario',
    passwordLabel: 'Contraseña',
    passwordPlaceholder: '••••••••',
    submitButton: 'Iniciar sesión',
    submitting: 'Iniciando sesión...',
    forgotPassword: '¿Olvidaste tu contraseña?',
    noAdminRights: 'No tienes derechos de administración',
    errorGeneric: 'Ocurrió un error durante el inicio de sesión',
    showPassword: 'Mostrar contraseña',
    hidePassword: 'Ocultar contraseña',
  },
  auth: {
    optional: 'opcional',
  },
  home: {
    title: '¡Vota por tus canciones favoritas en conciertos en vivo!',
    wantToVote: 'Quiero votar',
    wantToVoteDescription: 'Crea una cuenta gratuita para votar en conciertos',
    wantToManageBand: 'Gestiono mi grupo',
    wantToManageBandDescription: 'Crea una cuenta para gestionar tus eventos y canciones',
    alreadyHaveAccount: '¿Ya tienes una cuenta?',
  },
  landing: {
    languageLabel: 'Elige tu idioma',
    heroTitle: 'Deja que el público elija la siguiente canción de tu setlist',
    heroSubtitle:
      'Crea tu setlist. Los fans escanean un código QR y votan por la siguiente canción en directo. Una experiencia única en cada concierto.',
    ctaPrimary: 'Crear mi cuenta de grupo — prueba gratuita',
    whyTitle: '¿Por qué PlayItNow?',
    benefit1Title: 'Un QR, el público vota',
    benefit1Desc: 'Tu público escanea y vota por sus canciones favoritas desde el móvil. Más interacción.',
    benefit2Title: 'Ranking en directo',
    benefit2Desc: 'Los votos se muestran en tiempo real. Toca los temas más pedidos en el escenario.',
    benefit3Title: 'Simple para los grupos',
    benefit3Desc: 'Crea eventos, gestiona tu setlist, lanza la votación en un clic. Precios para artistas.',
    twoWaysTitle: 'Dos formas de vivir PlayItNow',
    ctaBand: 'Soy un grupo',
    ctaBandDesc: 'Crea eventos, añade tus canciones, deja que el público vote en directo.',
    ctaFan: 'Soy espectador',
    ctaFanDesc: 'Únete a un concierto con el código QR y vota por las canciones que quieres escuchar.',
    alreadyAccount: '¿Ya tienes cuenta?',
    login: 'Iniciar sesión',
    learnMore: 'Ver eventos',
  },
  public: {
    login: {
      title: 'PlayItNow',
      subtitle: '¡Vota por tus canciones favoritas!',
      subtitle2: '¡Los temas ganadores se tocarán en vivo!',
      identifyTitle: 'Identifícate',
      identifySubtitle: '¡Vota para elegir la próxima canción del concierto!',
      emailLabel: 'Email o nombre de usuario',
      emailPlaceholder: 'tu@email.com o nombre de usuario',
      passwordLabel: 'Contraseña',
      passwordPlaceholder: '••••••••',
      submitButton: 'Iniciar sesión',
      submitting: 'Iniciando sesión...',
      loading: 'Cargando...',
      withoutRegistration: 'Acceder a votación',
      or: 'O',
      errorGeneric: 'Ocurrió un error',
      showPassword: 'Mostrar contraseña',
      hidePassword: 'Ocultar contraseña',
      firstNameLabel: 'Tu nombre (opcional)',
      firstNamePlaceholder: 'Juan',
      firstNameHint: 'Tu nombre se mostrará cuando votes',
      haveAccount: 'Tengo una cuenta',
      forgotPassword: '¿Olvidaste tu contraseña?',
      createAccount: 'Crear una cuenta',
      pinDataHub: 'Gestionar mi banda',
      orDivider: 'o',
      loginWithGoogle: 'Iniciar sesión con Google',
      loginWithFacebook: 'Iniciar sesión con Facebook',
      oauthNoAccount: 'No hay cuenta asociada a este perfil. Elija « Cuenta fan » o « Cuenta grupo » en la página de inicio y continúe con Google o Facebook.',
      oauthFailed: 'Error al conectar con este proveedor. Inténtelo de nuevo.',
      oauthNoEmail: 'Facebook no ha proporcionado un email. Compruebe los permisos o use una cuenta con email, o regístrese con Google.',
      oauthUnavailable: 'La conexión con este proveedor no está disponible temporalmente.',
      oauthUseProvider: 'Use Google o Facebook para iniciar sesión.',
      oauthCompleteEmailTitle: 'Facebook no ha compartido tu email',
      oauthCompleteEmailDescription: 'Introduce tu dirección de correo para completar la conexión.',
      oauthCompleteEmailSubmit: 'Completar conexión',
      oauthCompleteEmailPlaceholder: 'tu@email.com',
      oauthCompleteEmailExpired: 'Sesión expirada. Vuelve a intentar con Facebook.',
      oauthCompleteEmailNoAccount: 'Elige primero un tipo de cuenta (fan o grupo) en la página de inicio.',
    },
    register: {
      title: 'Crear una cuenta',
      subtitle: 'Únete a la comunidad PlayItNow',
      firstNameLabel: 'Nombre',
      firstNamePlaceholder: 'Juan',
      lastNameLabel: 'Apellido',
      lastNamePlaceholder: 'García',
      emailLabel: 'Email',
      emailPlaceholder: 'tu@email.com',
      usernameLabel: 'Nombre de usuario',
      usernamePlaceholder: 'juan.garcia',
      autoGenerated: 'Generado automáticamente',
      usernameHint: 'Puedes modificar este nombre de usuario',
      submitButton: 'Crear mi cuenta',
      submitting: 'Creando cuenta...',
      backToLogin: 'Volver al inicio de sesión',
      orDivider: 'o',
      continueWithGoogle: 'Continuar con Google',
      continueWithFacebook: 'Continuar con Facebook',
      successTitle: '¡Registro exitoso!',
      successMessage:
        'Se ha enviado un correo de confirmación. Revisa tu bandeja de entrada para activar tu cuenta.',
      error: {
        firstNameRequired: 'El nombre es obligatorio',
        lastNameRequired: 'El apellido es obligatorio',
        emailRequired: 'El email es obligatorio',
        generic: 'Ocurrió un error al crear la cuenta',
      },
      // Band registration fields
      bandNameLabel: 'Nombre de la banda',
      bandNamePlaceholder: 'Nombre de tu banda',
      passwordLabel: 'Contraseña',
      passwordPlaceholder: '••••••••',
      passwordMinLength: 'La contraseña debe tener al menos 8 caracteres',
      logoUrlLabel: 'URL del logo (opcional)',
      logoUrlPlaceholder: 'https://example.com/logo.png',
      showPassword: 'Mostrar contraseña',
      hidePassword: 'Ocultar contraseña',
    },
    events: {
      title: 'Próximos eventos',
      noEvents: 'No hay eventos próximos',
      noEventsDescription: 'No hay eventos programados en los próximos 2 días.',
      errorLoad: 'Error al cargar eventos',
      settings: 'Configuración',
      logout: 'Salir',
    },
    event: {
      invalidSlug: 'Slug de evento inválido',
      notFound: 'Evento no encontrado',
      errorLoad: 'Error al cargar evento',
      backToEvents: 'Volver a eventos',
      songsTitle: 'Canciones disponibles',
      noSongs: 'No hay canciones disponibles en este momento',
      vote: 'Votar',
      voted: 'Votado',
      loading: 'Cargando...',
      votesRemaining: 'Votos restantes',
      voteSuccess: '¡Tu voto ha sido registrado!',
      voteError: 'Error al registrar tu voto',
      totalVotes: 'Total de votos',
      votesLabel: 'votos',
      blindVoteMessage: 'La clasificación será revelada por el grupo.',
      rankingTitle: 'Clasificación',
      votingPaused: 'Las votaciones están actualmente suspendidas.',
      votingCompleted: 'Este evento ha finalizado. Las votaciones están cerradas.',
      settings: 'Configuración',
      logout: 'Salir',
    },
    settings: {
      title: 'Configuración de la cuenta',
      account: 'Cuenta',
      email: 'Correo electrónico',
      username: 'Nombre de usuario',
      firstName: 'Nombre',
      lastName: 'Apellido',
      save: 'Guardar',
      saving: 'Guardando...',
      success: 'Configuración guardada con éxito',
      error: 'Error al guardar la configuración',
      password: {
        title: 'Cambiar contraseña',
        currentPassword: 'Contraseña actual',
        currentPasswordPlaceholder: '••••••••',
        newPassword: 'Nueva contraseña',
        newPasswordPlaceholder: '••••••••',
        confirmPassword: 'Confirmar nueva contraseña',
        confirmPasswordPlaceholder: '••••••••',
        change: 'Cambiar contraseña',
        changing: 'Cambiando...',
        success: 'Contraseña cambiada con éxito. Por favor, inicia sesión nuevamente.',
        error: 'Error al cambiar la contraseña',
        mismatch: 'Las contraseñas no coinciden',
        incorrect: 'La contraseña actual es incorrecta',
      },
      deleteAccount: {
        title: 'Eliminar cuenta',
        description: 'Eliminar tu cuenta es permanente y no se puede deshacer.',
        button: 'Eliminar mi cuenta',
        confirming: 'Confirmando...',
        confirmTitle: 'Confirmar eliminación',
        confirmMessage:
          '¿Estás seguro de que quieres eliminar tu cuenta? Esta acción es irreversible.',
        confirmButton: 'Sí, eliminar',
        cancelButton: 'Cancelar',
        success: 'Cuenta eliminada con éxito',
        error: 'Error al eliminar la cuenta',
      },
    },
    dataExport: {
      title: 'Exportar Sus Datos',
      description:
        'De acuerdo con el RGPD, puede solicitar la exportación de todos sus datos personales almacenados en PlayItNow.',
      whatIncluded: 'La exportación incluirá:',
      included: {
        profile: 'Su información de perfil (email, nombre, etc.)',
        votes: 'Su historial de votos',
        preferences: 'Sus preferencias (tema, idioma)',
        sessions: 'Sus sesiones de inicio de sesión',
      },
      exportButton: 'Exportar mis datos',
      exporting: 'Exportando...',
      success: 'Sus datos se han exportado con éxito. La descarga comenzará.',
      error: 'Ocurrió un error al exportar sus datos.',
      note: 'Nota: La exportación de sus datos puede tardar unos momentos. El archivo estará en formato JSON.',
    },
  },
  legal: {
    title: 'Aviso Legal',
    editor: {
      title: 'Editor',
      name: 'axialdata',
      email: 'Email: contact@axialdata.net',
      siret: 'SIRET: 49347625300023',
    },
    publication: {
      title: 'Director de Publicación',
      director: 'Pierre-Yves Léglise',
    },
    hosting: {
      title: 'Alojamiento',
      name: 'Hostinger International Ltd',
      addressLine1: 'La aplicación está alojada en Francia (centro de datos de París).',
      addressLine2: 'Sede social: Švitrigailos g. 34, LT-03230 Vilnius, Lituania',
      country: 'Lituania',
      url: 'https://www.hostinger.com',
    },
    dataProtection: {
      title: 'Protección de Datos',
      description:
        'De acuerdo con el Reglamento General de Protección de Datos (RGPD), tiene derechos sobre sus datos personales.',
      link: 'Consulte nuestra política de privacidad',
    },
  },
  privacy: {
    title: 'Política de Privacidad',
    lastUpdated: 'Última actualización: octubre 2025',
    intro: {
      title: 'Introducción',
      description:
        'PlayItNow se compromete a proteger su privacidad y datos personales. Esta política explica cómo recopilamos, utilizamos y protegemos su información.',
    },
    dataCollection: {
      title: 'Datos Recopilados',
      bands: {
        title: 'Para Grupos (Clientes de Pago)',
        email: 'Dirección de correo electrónico',
        username: 'Nombre de usuario',
        name: 'Nombre del grupo e información de contacto',
        payment: 'Información de pago (a través de proveedor tercero seguro)',
        logo: 'Logo del grupo',
      },
      spectators: {
        title: 'Para Espectadores (Usuarios Gratuitos)',
        email: 'Dirección de correo electrónico (si creación de cuenta)',
        firstName: 'Nombre (opcional, para mostrar durante votos)',
        fingerprint: 'Huella digital del navegador (anti-fraude)',
        ip: 'Dirección IP (para seguridad y auditoría)',
        votes: 'Historial de votos',
      },
    },
    purposes: {
      title: 'Finalidades del Tratamiento',
      authentication: 'Autenticación y gestión de cuentas de usuario',
      voting: 'Gestión del sistema de votación interactiva',
      fraud: 'Prevención de fraude y seguridad de votos',
      billing: 'Facturación y gestión de suscripciones (solo grupos)',
      support: 'Soporte al cliente y asistencia técnica',
      preferences: 'Almacenamiento de preferencias de usuario (tema, idioma)',
      analytics: 'Medición de audiencia del sitio (Rybbit autoalojado, uso interno: páginas vistas, procedencia; ningún dato vendido ni cedido)',
    },
    legalBasis: {
      title: 'Base Legal',
      description:
        'El tratamiento de sus datos se basa en: (1) ejecución de contrato para grupos, (2) su consentimiento para espectadores, (3) nuestro interés legítimo para seguridad y prevención de fraude.',
    },
    retention: {
      title: 'Duración de Conservación',
      active: 'Datos de cuenta activa: conservados durante toda la duración del uso del servicio',
      inactive: 'Datos de cuenta inactiva: eliminados después de 3 años de inactividad',
      legal:
        'Datos necesarios para obligaciones legales: conservados según períodos legales aplicables',
    },
    rights: {
      title: 'Sus Derechos',
      description: 'De acuerdo con el RGPD, tiene los siguientes derechos:',
      access: 'Derecho de acceso: consultar sus datos personales',
      rectification: 'Derecho de rectificación: corregir sus datos inexactos',
      deletion: 'Derecho al borrado: eliminar sus datos (derecho al olvido)',
      portability: 'Derecho a la portabilidad: recuperar sus datos en formato estructurado',
      opposition: 'Derecho de oposición: oponerse al tratamiento de sus datos',
      limitation: 'Derecho a la limitación: limitar el tratamiento de sus datos',
      contact:
        'Para ejercer sus derechos, contáctenos en: contact@axialdata.net. También puede exportar o eliminar sus datos desde Ajustes de la cuenta.',
    },
    security: {
      title: 'Seguridad de Datos',
      description:
        'Implementamos medidas técnicas y organizativas apropiadas para proteger sus datos contra acceso no autorizado, pérdida, destrucción o alteración. Los datos se almacenan en servidores seguros y las comunicaciones están cifradas.',
    },
    cookies: {
      title: 'Cookies y Rastreadores',
      description:
        'Nuestra aplicación utiliza cookies esenciales para el funcionamiento del servicio. Para más información, consulte nuestra política de cookies.',
      link: 'Política de Cookies',
    },
    contact: {
      title: 'Contacto',
      description: 'Para cualquier pregunta sobre esta política o sus datos personales:',
      email: 'contact@axialdata.net',
    },
  },
  cookies: {
    title: 'Política de Cookies',
    intro: {
      title: 'Introducción',
      description:
        'Esta página le informa sobre el uso de cookies y tecnologías similares en PlayItNow.',
    },
    essential: {
      title: 'Cookies Esenciales',
      description:
        'Las siguientes cookies son estrictamente necesarias para el funcionamiento del servicio y no requieren consentimiento previo:',
      accessToken: {
        title: 'access_token',
        description:
          'Cookie de autenticación que mantiene su sesión iniciada. Esta cookie es httpOnly (no accesible vía JavaScript) y segura.',
        duration: 'Duración: 15 minutos',
      },
      refreshToken: {
        title: 'refresh_token',
        description:
          'Cookie que renueva automáticamente su sesión. Esta cookie es httpOnly y segura.',
        duration: 'Duración: 7 días',
      },
      csrfToken: {
        title: 'csrf_token',
        description:
          'Cookie de protección contra ataques CSRF (Cross-Site Request Forgery). Esta cookie es accesible vía JavaScript para validación del lado del cliente.',
        duration: 'Duración: 7 días',
      },
    },
    localStorage: {
      title: 'Almacenamiento Local (localStorage)',
      description:
        'También utilizamos el almacenamiento local de su navegador para recordar sus preferencias:',
      theme: 'Tema de visualización (modo claro/oscuro)',
      lang: 'Idioma preferido',
      firstname: 'Nombre (para mostrar durante votos)',
      fingerprint: 'Huella digital del navegador (anti-fraude)',
    },
    analytics: {
      title: 'Medición de audiencia (Rybbit)',
      description:
        'Cuando la medición de audiencia está activada (producción), un script Rybbit, alojado en nuestros propios servidores (analytics.axialdata.app), envía datos mínimos (páginas vistas, procedencia) para analizar el uso del sitio, de uso estrictamente interno. Sin publicidad; no se venden ni ceden datos a terceros. Este tratamiento se basa en nuestro interés legítimo. Rybbit puede utilizar un identificador técnico (cookie o almacenamiento local) para deduplicar visitas. Puede bloquear scripts de terceros en su navegador para desactivar este seguimiento.',
    },
    management: {
      title: 'Gestión de Cookies',
      description:
        'Puede gestionar las cookies a través de la configuración de su navegador. Tenga en cuenta que desactivar las cookies esenciales impedirá el correcto funcionamiento de la aplicación.',
    },
  },
  terms: {
    title: 'Condiciones Generales de Uso',
    lastUpdated: 'Última actualización: octubre 2025',
    intro: {
      title: 'Artículo 1 - Objeto',
      description:
        'Estas Condiciones Generales de Uso (CGU) rigen el uso del servicio PlayItNow, una plataforma SaaS que permite a los grupos musicales ofrecer una experiencia interactiva a su público durante conciertos. Estas CGU se aplican tanto a grupos (clientes de pago) como a espectadores (usuarios gratuitos).',
    },
    service: {
      title: 'Artículo 2 - Descripción del Servicio',
      description:
        'PlayItNow es una aplicación web que permite a los grupos musicales crear eventos, gestionar una biblioteca de canciones y permitir al público votar por las canciones que desean escuchar. El servicio es de pago para grupos y gratuito para espectadores.',
    },
    forBands: {
      title: 'Artículo 3 - Condiciones Específicas para Grupos',
      subtitle:
        'Las siguientes condiciones se aplican únicamente a grupos que utilizan el servicio como clientes de pago:',
      registration: {
        title: '3.1 - Registro y Cuenta',
        description:
          'Para usar el servicio como grupo, debe crear una cuenta proporcionando información exacta y actualizada. Es responsable de la confidencialidad de sus credenciales y de todas las actividades realizadas desde su cuenta.',
      },
      subscription: {
        title: '3.2 - Suscripciones y Tarifas',
        description:
          'El acceso al servicio para grupos está sujeto a una suscripción de pago. Los precios, planes y condiciones de suscripción están disponibles en la plataforma. Las suscripciones se renuevan automáticamente a menos que las cancele.',
      },
      payment: {
        title: '3.3 - Pago',
        description:
          'Los pagos se realizan a través de un proveedor de pago seguro. En caso de incumplimiento del pago, el acceso al servicio puede ser suspendido. No se debe reembolso en caso de terminación anticipada, excepto según lo requerido por la ley.',
      },
      obligations: {
        title: '3.4 - Obligaciones',
        customer: {
          title: '3.4.1 - Obligaciones del Cliente',
          accuracy: 'Proporcionar información exacta y actualizada',
          security: 'Mantener la confidencialidad de sus credenciales',
          compliance: 'Cumplir con las leyes y regulaciones aplicables',
          prohibited: 'No utilizar el servicio para fines ilegales o fraudulentos',
        },
        provider: {
          title: '3.4.2 - Obligaciones del Proveedor',
          availability: 'Asegurar la disponibilidad del servicio en la medida de lo posible',
          support: 'Proporcionar soporte técnico dentro de los límites del servicio',
          security: 'Implementar medidas de seguridad apropiadas',
        },
      },
      intellectualProperty: {
        title: '3.5 - Propiedad Intelectual',
        description:
          'Todos los elementos del servicio PlayItNow (código, diseño, marcas, etc.) son propiedad exclusiva de axialdata. El uso del servicio no otorga ningún derecho de propiedad sobre estos elementos.',
      },
      termination: {
        title: '3.6 - Terminación',
        description:
          'Puede cancelar su suscripción en cualquier momento desde su área de cliente. axialdata se reserva el derecho de suspender o terminar cualquier cuenta en caso de incumplimiento de estas CGU.',
      },
      jurisdiction: {
        title: '3.7 - Ley Aplicable y Jurisdicción',
        description:
          'Estas CGU se rigen por la ley francesa. En caso de disputa, los tribunales franceses tienen jurisdicción exclusiva.',
      },
    },
    forSpectators: {
      title: 'Artículo 4 - Condiciones Específicas para Espectadores',
      subtitle:
        'Las siguientes condiciones se aplican únicamente a espectadores que utilizan el servicio de forma gratuita:',
      voting: {
        title: '4.1 - Sistema de Votación',
        description: 'Al participar en un evento, puede:',
        rules: {
          one: 'Votar por una o más canciones según las reglas definidas por el grupo',
          fraud: 'Respetar los límites de voto para evitar fraude',
          respect: 'Usar el servicio de manera respetuosa y conforme a su propósito',
        },
      },
      account: {
        title: '4.2 - Cuenta de Usuario',
        description:
          'La creación de una cuenta es opcional. Puede participar de forma anónima proporcionando solo un nombre. Si crea una cuenta, es responsable de la confidencialidad de sus credenciales.',
      },
      conduct: {
        title: '4.3 - Reglas de Conducta',
        description: 'Está estrictamente prohibido:',
        prohibited: {
          fraud: 'Intentar defraudar el sistema de votación',
          abuse: 'Usar el servicio de manera abusiva o maliciosa',
          illegal: 'Usar el servicio para fines ilegales',
        },
      },
      privacy: {
        title: '4.4 - Datos Personales',
        description:
          'El tratamiento de sus datos personales se rige por nuestra política de privacidad. Al usar el servicio, acepta esta política.',
        link: 'Consulte nuestra política de privacidad',
      },
    },
    liability: {
      title: 'Artículo 5 - Responsabilidad',
      description:
        'axialdata no será responsable de daños directos o indirectos resultantes del uso o la imposibilidad de usar el servicio. Para grupos, la responsabilidad se limita al monto de las sumas pagadas por la suscripción. Para espectadores, el servicio se proporciona "tal cual" sin garantía de ningún tipo.',
    },
    modifications: {
      title: 'Artículo 6 - Modificaciones',
      description:
        'axialdata se reserva el derecho de modificar estas CGU en cualquier momento. Las modificaciones entran en vigor al publicarse. Su uso continuo del servicio constituye la aceptación de las nuevas condiciones.',
    },
    contact: {
      title: 'Artículo 7 - Contacto',
      description: 'Para cualquier pregunta sobre estas CGU:',
      email: 'contact@axialdata.net',
    },
  },
  cookieBanner: {
    message:
      'Este sitio utiliza cookies esenciales y medición de audiencia (anónima, uso estrictamente interno). Al continuar navegando, acepta su uso.',
    learnMore: 'Saber más sobre las cookies',
    accept: 'Acepto',
  },
  footer: {
    legal: 'Aviso Legal',
    privacy: 'Privacidad',
    cookies: 'Cookies',
    terms: 'Términos',
  },
  errors: {
    EVENT_NOT_AVAILABLE_YET:
      'Este evento aún no está disponible. Vuelve en la fecha y hora previstas para acceder a la votación.',
    networkError:
      'No se puede conectar con el servidor. Comprueba tu conexión o inténtalo más tarde.',
    unexpectedError: 'Se ha producido un error inesperado. Inténtalo de nuevo más tarde.',
    validationError: 'Datos no válidos. Comprueba el formulario.',
    badRequest: 'Solicitud no válida.',
    unauthorized: 'Autenticación requerida.',
    forbidden: 'Acceso denegado.',
    notFound: 'Recurso no encontrado.',
    invalidJsonBody: 'Formato de datos no válido.',
    invalidCredentials: 'Credenciales incorrectas.',
    emailAlreadyRegistered: 'Esta dirección de correo ya está registrada.',
    notAuthenticated: 'Debes iniciar sesión.',
    tokenRequired: 'Enlace no válido o caducado.',
    ONE_SHOW_DATE_OUT_OF_RANGE:
      'La fecha del evento ONE_SHOW debe estar entre la fecha de compra y +30 días',
  },
  common: {
    copyright: 'Todos los derechos reservados',
    year: new Date().getFullYear().toString(),
    company: 'axialdata',
    required: 'Requerido',
    back: 'Volver',
    goHome: 'Ir al inicio',
    close: 'Cerrar',
    cancel: 'Cancelar',
    selectLanguage: 'Seleccionar idioma',
    skipToContent: 'Ir al contenido principal',
    activateDarkMode: 'Activar modo oscuro',
    activateLightMode: 'Activar modo claro',
    loading: 'Cargando...',
    processing: 'Procesando...',
    logoUrlExpiringModalTitle: 'La URL de la imagen puede caducar',
    logoUrlExpiringModalMessage:
      'Esta URL apunta a un host (p. ej. Facebook) cuyos enlaces de imágenes pueden volverse inválidos con el tiempo. La imagen podría dejar de mostrarse más adelante. Puede guardar esta URL si lo desea.',
    ok: 'OK',
  },
  pwa: {
    install: {
      button: 'Instalar aplicación',
      installButton: 'Instalar',
      promptDescription: 'Instala PlayItNow en tu dispositivo para una mejor experiencia',
      waiting: 'Esperando permiso de instalación...',
      iosButton: 'Instalar en iOS',
      iosTitle: 'Instalar PlayItNow en tu iPhone/iPad',
      iosStep1: '1. Toca el botón de compartir en la parte inferior de la pantalla',
      iosStep2: '2. Desplázate hacia abajo y selecciona "Añadir a la pantalla de inicio"',
      iosStep3: '3. Toca "Añadir" para instalar la aplicación',
    },
  },
  forgotPassword: {
    title: 'Contraseña olvidada',
    subtitle: 'Ingresa tu dirección de correo para recibir un enlace de restablecimiento',
    emailLabel: 'Dirección de correo',
    emailPlaceholder: 'tu@email.com',
    submitButton: 'Enviar enlace de restablecimiento',
    submitting: 'Enviando...',
    successMessage:
      'Si existe una cuenta con ese correo, se ha enviado un enlace de restablecimiento.',
    backToLogin: 'Volver al inicio de sesión',
    errorGeneric: 'Ocurrió un error',
  },
  resetPassword: {
    title: 'Restablecer contraseña',
    subtitle: 'Ingresa tu nueva contraseña',
    passwordLabel: 'Nueva contraseña',
    passwordPlaceholder: '••••••••',
    confirmPasswordLabel: 'Confirmar contraseña',
    confirmPasswordPlaceholder: '••••••••',
    submitButton: 'Restablecer contraseña',
    submitting: 'Restableciendo...',
    successMessage: 'Tu contraseña ha sido restablecida con éxito. Ahora puedes iniciar sesión.',
    passwordsDoNotMatch: 'Las contraseñas no coinciden',
    backToLogin: 'Volver al inicio de sesión',
    errorGeneric: 'Ocurrió un error durante el restablecimiento',
    invalidToken: 'El enlace de restablecimiento es inválido o ha expirado',
  },
  confirmEmail: {
    title: 'Confirma tu dirección de correo electrónico',
    setPasswordTitle: 'Define tu contraseña',
    setPasswordSubtitle: 'Elige una contraseña para completar tu registro.',
    passwordLabel: 'Contraseña',
    confirmPasswordLabel: 'Confirmar contraseña',
    submitButton: 'Activar mi cuenta',
    confirming: 'Confirmando…',
    successFan: 'Cuenta activada. Redirigiendo…',
    successBand: 'Cuenta creada. Redirigiendo…',
    alreadyCompleted: 'Inscripción ya completada o enlace expirado. Inicia sesión.',
    goToLogin: 'Ir a iniciar sesión',
    invalidToken: 'Enlace inválido o expirado. Regístrate de nuevo o solicita un nuevo correo.',
    missingToken: 'Falta el enlace de confirmación.',
    errorGeneric: 'Ocurrió un error.',
    backToRegister: 'Volver al registro',
  },
  admin: {
    nav: {
      dashboard: 'Panel de control',
      events: 'Eventos',
      songs: 'Canciones',
      venues: 'Lugares',
      members: 'Miembros',
      settings: 'Configuración',
      userSettings: 'Mi cuenta',
      logout: 'Cerrar sesión',
    },
    dashboard: {
      welcome: 'Bienvenido',
      bandName: 'Banda',
      superadminMode: 'Modo Super Administrador',
      totalEvents: 'Total eventos',
      activeEvents: 'Eventos activos',
      upcomingEvents: 'Próximos eventos',
      quickActions: 'Acciones rápidas',
      createEvent: 'Crear evento',
      manageVenues: 'Gestionar lugares',
      recentEvents: 'Eventos recientes',
      viewAll: 'Ver todo',
      noEvents: 'Aún no hay eventos',
      createFirstEvent: 'Crea tu primer evento',
      noVenue: 'Sin lugar',
      errorLoad: 'Error al cargar los datos',
      status: {
        pending: 'Pendiente',
        active: 'Activo',
        completed: 'Completado',
      },
    },
    planBadge: {
      tooltip: 'Plan actual — Haz clic para gestionar tu suscripción',
      ariaLabel: 'Ver y gestionar tu suscripción',
    },
    oneShowAlert: {
      title: 'Validez del evento OneShow',
      daysRemaining: '{{days}} día(s) restante(s)',
      daysShort: '{{days}}d',
      daysRemainingMessage: 'Te quedan {{days}} día(s) para usar este evento en modo ilimitado.',
      urgentTitle: 'Expiración inminente',
      expired: 'Evento OneShow expirado',
      expiredShort: 'Expirado',
      expiredMessage:
        'Este evento ha llegado al final de su período de validez. Pasa a ProGig para eventos ilimitados.',
      upgradeCta: 'Ver planes',
    },
    subscription: {
      plan: {
        FREE: 'Free',
        PRO_GIG: 'ProGig',
        LIVE_TOUR: 'LiveTour',
        ONE_SHOW: 'OneShow',
      },
      title: 'Suscripción',
      subtitle: 'Gestiona el plan de tu banda y tus límites de uso',
      currentPlan: 'Plan actual',
      currentPlanFree: 'Gratuito (FREE)',
      currentPlanProGig: 'ProGig',
      currentPlanLiveTour: 'LiveTour',
      freeDescription: 'Perfecto para descubrir y probar el servicio.',
      proGigDescription: 'Plan para bandas con varios conciertos al mes.',
      liveTourDescription: 'Plan para giras intensivas y uso ilimitado.',
      chooseProGig: 'Pasar a ProGig',
      chooseLiveTour: 'Pasar a LiveTour',
      loading: 'Cargando tu suscripción...',
      errorLoad: 'Se produjo un error al cargar tu suscripción.',
      errorCheckout:
        'Error al crear la sesión de pago de Stripe. Por favor, inténtalo de nuevo en unos instantes.',
      successTitle: 'Pago realizado con éxito',
      successMessage: 'Tu pago ha sido procesado. Tu suscripción se actualizará en unos instantes.',
      successRedirect: 'Serás redirigido a tu panel de control en breve.',
      monthlyQuota: 'Cuota mensual',
      freeQuota: 'Cuota gratuita',
      eventsUsed: '{{current}} / {{limit}} eventos',
      billingPeriod: 'Período: {{start}} - {{end}}',
      quotaReached: 'Cuota mensual alcanzada',
      quotaReachedMessage:
        'Has alcanzado tu límite de eventos para este período. La cuota se restablecerá al final de tu período de facturación.',
      quotaWarning: 'Cuota mensual casi alcanzada',
      quotaWarningMessage: 'Te quedan {{remaining}} evento(s) para este período.',
      quotaInfo: 'Te quedan {{remaining}} evento(s) para este período.',
      upgradePlan: 'Mejorar plan',
      upgradeToUnlimited: 'Pasar a ilimitado',
      upgradeSingleEventConfirm: 'Estás a punto de pasar el evento "{{eventName}}" a ilimitado.',
      singleEventDescription: 'Es un pago único para votos ilimitados en este evento específico.',
      oneShowValidity: 'Válido {{days}} días desde la compra.',
      oneShowNonRefundable:
        'Pago no reembolsable: una vez realizado, el pago no podrá ser cancelado ni reembolsado.',
      multipleProGigSuggestion: '¿Varios eventos? El plan ProGig podría ser más rentable.',
      comparePlans: 'Comparar planes →',
      upgradeToUnlimitedNow: 'Pasar a ilimitado ahora',
      checkoutError: 'Error al crear la sesión de pago. Inténtalo de nuevo.',
      oneShowPrice: 'Precio',
      oneShowPaymentType: 'Pago único',
      oneShowBenefitsTitle: 'Lo que obtienes:',
      oneShowBenefitVotes: 'Votos ilimitados para este evento',
      oneShowBenefitTime: 'Sin límite de tiempo activo',
      chooseOption: 'Elegir una opción',
      convertEvent: 'Convertir un evento',
      createEvent: 'Crear un evento',
      selectFreeEvent: 'Seleccione un evento gratuito para convertir:',
      convertThisEvent: 'Convertir este evento',
      noFreeEventsToConvert: 'Sin eventos gratuitos para convertir. Cree un evento primero.',
      createDescription: 'Cree un nuevo evento con el plan One Show.',
      createNewOneShot: 'Crear evento One Show',
      createOneShotEventTitle: 'Crear evento One Show',
      proceedToPayment: 'Proceder al pago',
      oneShowDescription: 'Pago único para un evento ilimitado.',
      configureOneShot: 'Configurar',
      convertEventFromUrl: 'El evento "{{name}}" se convertirá en evento One Show.',
      priceFree: 'Gratis',
      priceOneTime: '{{price}}€ (pago único)',
      pricePerMonth: '{{price}}€ / mes',
      currentPlanBadge: 'Plan actual',
      selectPlan: 'Elegir',
      planFree: 'Gratis',
      featureMaxEvents: 'Eventos máximo',
      featureMaxVotes: 'Votos por evento',
      featureActiveTime: 'Tiempo de activación acumulado',
      featureEventsPerMonth: 'Eventos por mes',
      featureOneEvent: '1 evento (único)',
      featureValidity: 'Validez',
      validityDays: '{{days}} días',
      unlimited: 'Ilimitado',
      events: 'eventos',
      votes: 'votos',
      minutes: 'min',
      planNote: {
        FREE: '5 eventos máx (excl. OneShow). 8 votos por evento. 40 min de activación acumulada.',
        ONE_SHOW: 'Pago único. Un evento ilimitado (votos ilimitados, sin límite de tiempo).',
        PRO_GIG: '3 eventos por mes (período rodante según facturación Stripe).',
        LIVE_TOUR: 'Eventos ilimitados. Ideal para giras.',
      },
      cancelSubscriptionButton: 'Cancelar suscripción',
      cancelSubscriptionConfirmTitle: 'Cancelar suscripción',
      cancelSubscriptionConfirmMessage:
        'Sin reembolso. Tu suscripción sigue activa hasta el {{date}} (fin del período ya pagado), después pasarás al plan gratuito. ¿Continuar?',
      cancelSubscriptionSuccess: 'Suscripción cancelada. Conservas el acceso hasta el {{date}}.',
      cancelSubscriptionError: 'Error al cancelar la suscripción.',
      accessUntil: 'Acceso hasta el {{date}}',
      cancellationScheduled: 'Cancelación programada',
      cancelling: 'Cancelando...',
    },
    settings: {
      title: 'Configuración de la banda',
      subtitle: 'Gestiona la información de tu banda',
      bandName: 'Nombre de la banda',
      bandNamePlaceholder: 'Nombre de tu banda',
      logoUrl: 'URL del logo',
      logoUrlPlaceholder: 'https://example.com/logo.png',
      logoPreview: 'Vista previa del logo',
      save: 'Guardar',
      saving: 'Guardando...',
      successMessage: 'Configuración guardada con éxito',
      errorLoad: 'Error al cargar la configuración',
      errorSave: 'Error al guardar la configuración',
    },
    userSettings: {
      title: 'Mi cuenta',
      account: 'Información de la cuenta',
      email: 'Correo electrónico',
      username: 'Nombre de usuario',
      firstName: 'Nombre',
      lastName: 'Apellido',
      save: 'Guardar cambios',
      saving: 'Guardando...',
      success: 'Configuración de la cuenta actualizada con éxito',
      error: 'Error al actualizar la cuenta',
      password: {
        title: 'Cambiar contraseña',
        currentPassword: 'Contraseña actual',
        currentPasswordPlaceholder: '••••••••',
        newPassword: 'Nueva contraseña',
        newPasswordPlaceholder: '••••••••',
        confirmPassword: 'Confirmar nueva contraseña',
        confirmPasswordPlaceholder: '••••••••',
        change: 'Cambiar contraseña',
        changing: 'Cambiando...',
        success: 'Contraseña cambiada con éxito. Por favor, inicia sesión de nuevo.',
        error: 'Error al cambiar la contraseña',
        mismatch: 'Las contraseñas no coinciden',
        incorrect: 'La contraseña actual es incorrecta',
      },
      deleteAccount: {
        title: 'Eliminar cuenta',
        description: 'Eliminar tu cuenta es una acción irreversible. Todos tus datos se perderán.',
        button: 'Eliminar mi cuenta',
        confirmTitle: 'Confirmar eliminación',
        confirmMessage:
          '¿Estás seguro de que quieres eliminar tu cuenta? Esta acción es irreversible.',
        confirmButton: 'Sí, eliminar',
        cancelButton: 'Cancelar',
        confirming: 'Eliminando...',
        error: 'Error al eliminar la cuenta',
      },
    },
    members: {
      title: 'Miembros de la banda',
      subtitle: 'Gestiona los miembros de tu banda',
      membersList: 'Lista de miembros',
      noMembers: 'Aún no hay miembros',
      inviteMember: 'Invitar miembro',
      invite: 'Invitar',
      inviting: 'Invitar...',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      confirmRemoveTitle: 'Eliminar miembro',
      confirmToggleOwnerTitle: 'Cambiar estado de propietario',
      email: 'Email',
      emailPlaceholder: 'miembro@example.com',
      password: 'Contraseña',
      passwordPlaceholder: 'Mínimo 8 caracteres',
      firstName: 'Nombre',
      firstNamePlaceholder: 'Nombre (opcional)',
      lastName: 'Apellido',
      lastNamePlaceholder: 'Apellido (opcional)',
      owner: 'Propietario',
      invitedBy: 'Invitado por',
      remove: 'Eliminar',
      confirmRemove: '¿Estás seguro de que quieres eliminar {{email}} de la banda?',
      inviteSuccess: 'Miembro invitado con éxito',
      inviteError: 'Error al invitar miembro',
      removeSuccess: 'Miembro eliminado con éxito',
      removeError: 'Error al eliminar miembro',
      errorLoad: 'Error al cargar los miembros',
      promote: 'Promover a propietario',
      demote: 'Degradar',
      confirmToggleOwner: '¿Estás seguro de que quieres {{action}} a este miembro?',
      promoteSuccess: 'Miembro promovido a propietario con éxito',
      demoteSuccess: 'Miembro degradado con éxito',
      toggleOwnerError: 'Error al cambiar el estado del miembro',
    },
    events: {
      title: 'Eventos',
      subtitle: 'Gestiona tus conciertos y eventos',
      eventsList: 'Lista de eventos',
      upcomingEvents: 'Próximos eventos',
      pastEvents: 'Eventos pasados',
      noEvents: 'Aún no hay eventos',
      addEvent: 'Crear evento',
      add: 'Crear',
      adding: 'Creando...',
      edit: 'Editar',
      editing: 'Editando...',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      confirmDeleteTitle: 'Eliminar evento',
      confirmDelete: '¿Estás seguro de que quieres eliminar el evento "{{name}}"?',
      name: 'Nombre del evento',
      namePlaceholder: 'Ej: Concierto en Zenith',
      venue: 'Lugar',
      selectVenue: 'Seleccionar un lugar',
      date: 'Fecha',
      time: 'Hora',
      oneShowDateRestriction: 'Fecha bloqueada: entre la compra y +{{days}} días',
      maxVotesPerUser: 'Votos máximos por usuario',
      maxVotes: 'Votos máx',
      tiebreaker: 'Desempate',
      tiebreakerChronological: 'Cronológico (primero votado)',
      tiebreakerRandom: 'Aleatorio',
      tiebreakerManual: 'Manual (elección del grupo)',
      showRanking: 'Mostrar clasificación',
      showRankingToPublic: 'Mostrar clasificación al público',
      blindVote: 'Voto ciego',
      status: {
        pending: 'Pendiente',
        active: 'Activo',
        completed: 'Completado',
      },
      planBadge: {
        subscription: 'Suscripción (ilimitado)',
        oneShow: 'OneShow (evento ilimitado)',
      },
      addSuccess: 'Evento creado con éxito',
      addError: 'Error al crear evento',
      updateSuccess: 'Evento actualizado con éxito',
      updateError: 'Error al actualizar evento',
      deleteSuccess: 'Evento eliminado con éxito',
      deleteError: 'Error al eliminar evento',
      errorLoad: 'Error al cargar eventos',
      noVenues: 'Debes crear al menos un lugar antes de crear un evento',
      qrCodeTitle: 'Código QR generado',
      qrCodeDescription: 'Usa este código para permitir que los espectadores voten',
      copyQRCode: 'Copiar código QR',
      qrCodeCopied: 'Código QR copiado al portapapeles',
      showQRCode: 'Mostrar código QR',
      qrCodePrint: 'Imprimir',
      qrCodeShare: 'Compartir',
      qrCodeDownload: 'Descargar',
      qrCodeUrl: 'URL del evento',
      qrCodeShareText: '¡Únete para votar por las canciones de {{eventName}}!',
      qrCodePrintTitle: 'Código QR - PlayItNow',
      qrCodePrintInstructions:
        'Escanea este código QR para acceder al evento y votar por tus canciones favoritas. ¡Las canciones ganadoras se tocarán en vivo!',
      delete: 'Eliminar',
      activate: 'Activar',
      deactivate: 'Desactivar',
      complete: 'Completar',
      reactivate: 'Reactivar',
      activateSuccess: 'Evento activado con éxito',
      activateError: 'Error al activar evento',
      deactivateSuccess: 'Evento desactivado con éxito',
      deactivateError: 'Error al desactivar evento',
      completeSuccess: 'Evento completado con éxito',
      completeError: 'Error al completar evento',
      showRankingBtn: 'Mostrar clasificación a los espectadores',
      hideRankingBtn: 'Ocultar clasificación a los espectadores',
      rankingHidden: 'Clasificación oculta a los espectadores',
      rankingVisible: 'Clasificación visible para los espectadores',
      toggleBlindVoteError: 'Error al cambiar la visibilidad de la clasificación',
      live: {
        title: 'Dashboard en vivo',
        loading: 'Cargando...',
        errorLoad: 'Error al cargar datos',
        errorToggle: 'Error al cambiar visibilidad',
        errorMarkPlayed: 'Error al marcar canción',
        eventNotFound: 'Evento no encontrado',
        backToEvents: 'Volver a eventos',
        totalVotes: 'Votos totales',
        uniqueVoters: 'Votantes únicos',
        songsPlayed: 'Canciones tocadas',
        noVotesYet: 'Sin votos todavía',
        noVotes: 'Sin votos',
        rankingHidden: 'Clasificación oculta',
        rankingVisible: 'Clasificación visible',
        markPlayed: 'Marcar como tocada',
        unmarkPlayed: 'Desmarcar',
        autoRefresh: 'Actualización auto',
        openLive: 'Abrir Live',
      },
      songs: {
        title: 'Canciones del evento',
        subtitle: 'Gestiona las canciones disponibles para votar',
        addSong: 'Agregar canción',
        searchExisting: 'Buscar canción existente',
        searchPlaceholder: 'Buscar por título o artista...',
        noResults: 'No se encontraron canciones',
        or: 'O',
        songTitle: 'Título',
        titlePlaceholder: 'Ej: Bohemian Rhapsody',
        artist: 'Artista',
        artistPlaceholder: 'Ej: Queen',
        duration: 'Duración',
        durationPlaceholder: 'Ej: 5:55',
        year: 'Año',
        yearPlaceholder: 'Ej: 2020',
        add: 'Agregar',
        adding: 'Agregando...',
        remove: 'Eliminar',
        noSongs: 'No hay canciones para este evento',
        addSuccess: 'Canción agregada con éxito',
        addError: 'Error al agregar canción',
        createSuccess: 'Canción creada y agregada con éxito',
        createError: 'Error al crear canción',
        removeSuccess: 'Canción eliminada con éxito',
        removeError: 'Error al eliminar canción',
        errorLoad: 'Error al cargar canciones',
        copyFromEvent: 'Copiar desde otro evento',
        copyModal: {
          title: 'Copiar lista de canciones',
          description: 'Seleccione un evento para copiar su lista de canciones a este evento.',
          noEvents: 'No hay otros eventos disponibles',
        },
        copySuccess: 'Canciones copiadas con éxito',
        copySuccessCount: 'canción(es) agregada(s)',
        copySkipped: 'ya presente(s)',
        copyNoSongs: 'El evento seleccionado no contiene canciones',
        copyAllAlreadyAdded: 'Todas las canciones de este evento ya están presentes',
        copyError: 'Error al copiar canciones',
      },
    },
    songs: {
      title: 'Biblioteca de canciones',
      subtitle: 'Gestiona tu repertorio de canciones',
      songsList: 'Lista de canciones',
      noSongs: 'Aún no hay canciones',
      addSong: 'Agregar canción',
      add: 'Agregar',
      adding: 'Agregando...',
      edit: 'Editar',
      editing: 'Editando...',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      confirmDeleteTitle: 'Eliminar canción',
      confirmDelete: '¿Estás seguro de que quieres eliminar "{{title}}" de {{artist}}?',
      songTitle: 'Título',
      titlePlaceholder: 'Ej: Bohemian Rhapsody',
      artist: 'Artista',
      artistPlaceholder: 'Ej: Queen',
      duration: 'Duración',
      durationPlaceholder: 'Ej: 5:55',
      durationFormat: 'Formato: MM:SS (ej: 3:45)',
      year: 'Año',
      yearPlaceholder: 'Ej: 2020',
      addSuccess: 'Canción agregada con éxito',
      addError: 'Error al agregar canción',
      updateSuccess: 'Canción actualizada con éxito',
      updateError: 'Error al actualizar canción',
      deleteSuccess: 'Canción eliminada con éxito',
      deleteError: 'Error al eliminar canción',
      errorLoad: 'Error al cargar canciones',
      delete: 'Eliminar',
    },
    venues: {
      title: 'Lugares de concierto',
      subtitle: 'Gestiona los lugares donde actúas',
      venuesList: 'Lista de lugares',
      noVenues: 'Aún no hay lugares',
      addVenue: 'Agregar lugar',
      add: 'Agregar',
      adding: 'Agregando...',
      edit: 'Editar',
      editing: 'Editando...',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      confirmDeleteTitle: 'Eliminar lugar',
      confirmDelete: '¿Estás seguro de que quieres eliminar el lugar "{{name}}" ?',
      name: 'Nombre del lugar',
      namePlaceholder: 'Ej: El Zénith',
      address: 'Dirección',
      addressPlaceholder: 'Ej: 211 Avenida Jean Jaurès',
      city: 'Ciudad',
      cityPlaceholder: 'Ej: París',
      country: 'País',
      countryPlaceholder: 'Ej: Francia',
      coordinates: 'Coordenadas GPS (opcional)',
      latitude: 'Latitud',
      latitudePlaceholder: 'Ej: 48.8566',
      longitude: 'Longitud',
      longitudePlaceholder: 'Ej: 2.3522',
      addSuccess: 'Lugar agregado con éxito',
      addError: 'Error al agregar lugar',
      updateSuccess: 'Lugar actualizado con éxito',
      updateError: 'Error al actualizar lugar',
      deleteSuccess: 'Lugar eliminado con éxito',
      deleteError: 'Error al eliminar lugar',
      errorLoad: 'Error al cargar los lugares',
    },
  },
  notFound: {
    title: 'Página no encontrada',
    subtitle: '¡Ups! Esta página no existe',
    description: 'La página que buscas puede haber sido movida o eliminada.',
    quickLinks: 'Enlaces rápidos',
    goHome: 'Ir al inicio',
    goToLogin: 'Ir al inicio de sesión',
    helpText: 'Si crees que esto es un error, por favor contacta al soporte.',
  },
}
